/**
 * Image Optimize API Functions (Structured Analysis)
 */

import { apiClient } from '../api-client';

export interface DetectedColor {
  name: string;
  hex: string;
  percentage: number;
}

export interface DetectedElement {
  type: 'product' | 'background' | 'text' | 'badge' | 'graphic' | 'icon' | 'person';
  description: string;
}

export interface AnalysisComposition {
  product_placement: string;
  negative_space: string;
  props_present: boolean;
  humans_present: boolean;
}

export interface AnalysisMood {
  primary_mood: string;
  energy: string;
  season_vibe: string;
  keywords: string[];
}

export interface OptimizeAnalysisResult {
  detected_text: string[];
  dominant_colors: DetectedColor[];
  tags: string[];
  elements: DetectedElement[];
  style_category: string;
  composition: AnalysisComposition;
  mood_and_atmosphere: AnalysisMood;
  style_dna_summary: string;
}

export interface AnalyzeParams {
  tags_quantity?: number;
  include_elements?: boolean;
  language?: string;
}

export interface OptimizeHistoryItem {
  id: string;
  timestamp: string;
  action: string;
  result: OptimizeAnalysisResult;
}

export async function analyzeImageOptimize(
  file: File,
  params: AnalyzeParams = {}
): Promise<OptimizeAnalysisResult> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('tags_quantity', String(params.tags_quantity ?? 8));
  formData.append('include_elements', String(params.include_elements ?? true));
  if (params.language) formData.append('language', params.language);
  const response = await apiClient.post<OptimizeAnalysisResult>(
    '/api/v2/image-optimize/analyze',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
}

export async function getOptimizeHistory(
  limit = 10
): Promise<OptimizeHistoryItem[]> {
  const response = await apiClient.get<OptimizeHistoryItem[]>(
    '/api/v2/image-optimize/history',
    { params: { limit } }
  );
  return response.data;
}

export interface RetouchParams {
  action: 'recolor' | 'add' | 'remove' | 'custom';
  prompt?: string;
  color?: string;
  analysis_context?: string;
  language?: string;
  source_s3_key?: string;
}

export interface RetouchResult {
  image_url: string;
  s3_key: string;
}

export async function retouchImageOptimize(
  file: File,
  params: RetouchParams
): Promise<RetouchResult> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('action', params.action);
  if (params.prompt) formData.append('prompt', params.prompt);
  if (params.color) formData.append('color', params.color);
  if (params.analysis_context) formData.append('analysis_context', params.analysis_context);
  if (params.language) formData.append('language', params.language);
  if (params.source_s3_key) formData.append('source_s3_key', params.source_s3_key);
  const response = await apiClient.post<RetouchResult>(
    '/api/v2/image-optimize/retouch',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
}
